/**
 * TCG-Cases: dauerhaft sichtbare Header-Suche mit Vorschlägen direkt unter dem Feld.
 * Eigenständig (nutzt nur Shopifys Predictive Search API /search/suggest.json),
 * damit Horizon-Updates diese Datei nicht berühren.
 *
 * Maßsuche: Gibt der Kunde drei Maße ins Suchfeld ein (z. B. „190 x 170 x 90“, „19×17×9 cm“),
 * erscheinen statt der normalen Vorschläge sofort die passendsten Cases.
 *
 * Maßfelder: Klickt der Kunde ins leere Suchfeld, erscheint im Aufklapp-Fenster
 * eine Box mit drei Feldern (Länge × Breite × Höhe), ohne Button.
 * Sucht der Kunde nach Text, bleibt die Box oben über den Vorschlägen stehen.
 * Sobald drei gültige Maße eingegeben sind (Box oder Suchfeld), erscheinen die bis zu
 * 3 passendsten Cases sofort darunter – ohne Klick (Titel, Innenmaß des Cases, Preis; ein Case passt,
 * wenn jedes Innenmaß 1–5 mm größer ist).  Abgleich serverseitig: zuerst über die
 * Maß-Datenbank (App-Proxy /apps/massfinder → lager.tcg-cases.de, eine Anfrage), bei Ausfall über den
 * Abschnitt tcg-massfinder-results (Metafelder). Innenmaße bleiben in beiden Fällen nicht öffentlich.
 * Markup und Styles werden hier per JS erzeugt – keine Horizon-Datei wird geändert.
 * Texte: Einstellungen des Abschnitts „TCG Suche (Header)“ (#tcg-dim-strings),
 * übersetzbar mit Translate & Adapt. Die Werte unten sind nur Notfall-Fallbacks.
 */
if (!customElements.get('tcg-header-search')) {
  const TCG_DIM_NUM = '(\\d{1,4}(?:[.,]\\d{1,2})?)';
  const TCG_DIM_UNIT = '\\s*(mm|cm)?\\s*';
  const TCG_DIM_SEP = '(?:\\s*[x×*\\/]\\s*|\\s+)';
  const TCG_DIM_RE = new RegExp(
    '^\\s*' + TCG_DIM_NUM + TCG_DIM_UNIT + TCG_DIM_SEP + TCG_DIM_NUM + TCG_DIM_UNIT + TCG_DIM_SEP + TCG_DIM_NUM + TCG_DIM_UNIT + '$',
    'i'
  );

  const TCG_DIM_FALLBACK = {
    title: 'Kennst Du die Maße Deines Sammelobjekts?',
    sub: 'Länge × Breite × Höhe in mm – die Reihenfolge ist egal.',
    length: 'Länge',
    width: 'Breite',
    height: 'Höhe',
    error: 'Bitte gib drei Maße zwischen 1 und 2000 mm ein.',
    liveHeading: 'Passende Cases',
    liveLoading: 'Suche passende Cases …',
    liveInner: 'Innenmaß __SIZE__',
    liveMore: 'Nicht passend genug?',
    liveNone: 'Leider passt kein Standard-Case – wir fertigen Dir eins nach Maß.',
    liveCustom: 'Zur Sonderanfertigung',
    customUrl: '/products/sonderanfertigung-acryl-case',
    liveDev: '__NAME__ __DEV__',
    liveExact: 'passgenau',
  };
  const TCG_DIM_MAX = 3;

  const TCG_DIMBOX_CSS = `
    .tcg-dimbox { padding: 14px 14px 12px; border-bottom: 1px solid var(--color-border); }
    .tcg-dimbox[hidden] { display: none; }
    .tcg-dimbox--bottom { border-bottom: 0; border-top: 1px solid var(--color-border); }
    .tcg-search__results:empty + .tcg-dimbox--bottom { border-top: 0; }
    .tcg-dimbox:has(+ .tcg-search__results:empty) { border-bottom: 0; }
    .tcg-search__panel .tcg-search__results:empty { display: none; }
    .tcg-dimbox__title { margin: 0; font-family: var(--font-subheading--family); font-size: 0.9375rem; font-weight: 600; line-height: 1.3; }
    .tcg-dimbox__sub { margin: 2px 0 10px; font-size: 0.8125rem; opacity: 0.65; }
    .tcg-dimbox__row { display: flex; align-items: center; gap: 6px; }
    .tcg-dimbox__input { flex: 1 1 0; min-width: 0; height: 40px; padding: 0 6px; border: 1px solid rgb(var(--color-foreground-rgb) / 0.25); border-radius: var(--style-border-radius-inputs, 4px); background: var(--color-background); color: var(--color-foreground); font-family: var(--font-body--family); font-size: 0.9375rem; text-align: center; appearance: none; }
    .tcg-dimbox__input:focus { outline: none; border-color: var(--color-foreground); box-shadow: 0 0 0 1px var(--color-foreground); }
    .tcg-dimbox__input::placeholder { color: rgb(var(--color-foreground-rgb) / 0.5); opacity: 1; }
    .tcg-dimbox__x, .tcg-dimbox__unit { flex: 0 0 auto; font-size: 0.875rem; opacity: 0.55; }
    .tcg-dimbox__err { margin: 8px 0 0; font-size: 0.8125rem; color: #b00020; }
    .tcg-dimbox__err[hidden] { display: none; }
    .tcg-search__fit { font-size: 0.8125rem; opacity: 0.7; }
    /* Mobil: Öffnet die Tastatur, scrollt die Seite – Horizons „beim Hochscrollen einblenden“-Header
       würde sich dann ausblenden und die Suche mitnehmen. Solange die Suche offen/fokussiert ist,
       bleibt der Header sichtbar. Nur eigene CSS-Regeln, keine Horizon-Datei geändert. */
    .header-section:has(> #header-component[sticky] tcg-header-search:is([open], :focus-within)) {
      position: sticky !important;
      top: -1px;
      z-index: var(--layer-menu-drawer);
    }
    #header-component[sticky]:has(tcg-header-search:is([open], :focus-within)) {
      opacity: 1 !important;
    }
    @media screen and (max-width: 749px) {
      .tcg-dimbox__row { flex-wrap: wrap; }
      .tcg-dimbox__input { font-size: 16px; }
    }
  `;

  class TcgHeaderSearch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-tcg-search-input]');
      this.panel = this.querySelector('[data-tcg-search-panel]');
      this.list = this.querySelector('[data-tcg-search-results]');
      this.clearButton = this.querySelector('[data-tcg-search-clear]');
      this.suggestUrl = this.dataset.suggestUrl;
      this.searchUrl = this.dataset.searchUrl;
      this.t = JSON.parse(this.querySelector('[data-tcg-search-strings]')?.textContent || '{}');
      let dimStrings = {};
      try {
        dimStrings = JSON.parse(document.getElementById('tcg-dim-strings')?.textContent || '{}');
      } catch {}
      this.ds = {};
      for (const key of Object.keys(TCG_DIM_FALLBACK)) this.ds[key] = dimStrings[key] || TCG_DIM_FALLBACK[key];
      this.buildDimbox();
      this.cache = new Map();
      this.activeIndex = -1;
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.input.addEventListener('input', () => this.onInput(), { signal });
      this.input.addEventListener('focus', () => this.onFocus(), { signal });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.form?.addEventListener('submit', (event) => {
        const dims = this.dims(this.term);
        if (dims) {
          event.preventDefault();
          clearTimeout(this.dimTimer);
          this.liveDims(dims);
        }
      }, { signal });
      this.clearButton?.addEventListener('click', () => this.clear(), { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.close();
      }, { signal });
      window.addEventListener('resize', () => this.fitPanel(), { signal, passive: true });
    }

    /** Box mit drei Maßfeldern immer oben im Aufklapp-Fenster (leeres Suchfeld und Textsuche). */
    buildDimbox() {
      if (!document.getElementById('tcg-dimbox-style')) {
        const style = document.createElement('style');
        style.id = 'tcg-dimbox-style';
        style.textContent = TCG_DIMBOX_CSS;
        document.head.append(style);
      }
      const ds = this.ds;
      const box = this.el('form', 'tcg-dimbox');
      box.hidden = true;
      box.noValidate = true;
      box.setAttribute('role', 'search');
      box.setAttribute('aria-label', ds.title);
      box.append(
        this.el('p', 'tcg-dimbox__title', ds.title),
        this.el('p', 'tcg-dimbox__sub', ds.sub)
      );
      const row = this.el('div', 'tcg-dimbox__row');
      const names = [ds.length, ds.width, ds.height];
      this.dimInputs = names.map((name, i) => {
        const input = this.el('input', 'tcg-dimbox__input');
        input.type = 'text';
        input.inputMode = 'decimal';
        input.autocomplete = 'off';
        input.maxLength = 7;
        input.placeholder = name;
        input.setAttribute('aria-label', `${name} (mm)`);
        row.append(input);
        if (i < 2) row.append(this.el('span', 'tcg-dimbox__x', '×'));
        return input;
      });
      row.append(this.el('span', 'tcg-dimbox__unit', 'mm'));
      this.dimErr = this.el('p', 'tcg-dimbox__err');
      this.dimErr.hidden = true;
      this.dimErr.setAttribute('role', 'alert');
      box.append(row, this.dimErr);
      const readBox = () => this.dimInputs.map((input) => {
        const v = input.value.trim().replace(/mm$/i, '').trim().replace(',', '.');
        return /^\d+(\.\d{1,2})?$/.test(v) ? parseFloat(v) : NaN;
      });
      box.addEventListener('input', () => {
        this.dimErr.hidden = true;
        clearTimeout(this.dimTimer);
        const values = readBox();
        if (values.every((n) => n >= 1 && n <= 2000)) {
          this.dimTimer = setTimeout(() => this.liveDims(values), 350);
        } else {
          this.liveRun = (this.liveRun || 0) + 1;
          if (this.term.length === 0) this.list.replaceChildren();
        }
      });
      box.addEventListener('submit', (event) => {
        event.preventDefault();
        const values = readBox();
        if (!values.every((n) => n >= 1 && n <= 2000)) {
          this.dimErr.textContent = ds.error;
          this.dimErr.hidden = false;
          return;
        }
        this.dimErr.hidden = true;
        clearTimeout(this.dimTimer);
        this.liveDims(values);
      });
      box.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          this.input.focus();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          box.requestSubmit();
        }
      });
      this.dimbox = box;
      this.panel.prepend(box);
    }

    showDimbox() {
      this.activeIndex = -1;
      this.list.replaceChildren();
      this.placeDimbox('top');
      this.open();
    }

    /** Box oben (leeres Suchfeld) oder unten (unter den Text-Vorschlägen) einblenden. */
    placeDimbox(position) {
      if (!this.dimbox) return;
      const bottom = position === 'bottom';
      if (bottom && this.dimbox.previousElementSibling !== this.list) this.panel.append(this.dimbox);
      if (!bottom && this.panel.firstElementChild !== this.dimbox) this.panel.prepend(this.dimbox);
      this.dimbox.classList.toggle('tcg-dimbox--bottom', bottom);
      this.dimbox.hidden = false;
    }

    hideDimbox() {
      if (this.dimbox) this.dimbox.hidden = true;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.fetchController?.abort();
      clearTimeout(this.timer);
      this._ready = false;
    }

    get term() {
      return this.input.value.trim();
    }

    /** Erkennt drei Maße; gibt sie in mm zurück oder null. */
    dims(term) {
      const m = TCG_DIM_RE.exec(term || '');
      if (!m) return null;
      const cm = [m[2], m[4], m[6]].some((u) => u && u.toLowerCase() === 'cm');
      const values = [m[1], m[3], m[5]].map((v) => {
        const n = parseFloat(v.replace(',', '.'));
        return Math.round((cm ? n * 10 : n) * 100) / 100;
      });
      return values.every((n) => n >= 1 && n <= 2000) ? values : null;
    }

    /** Drei Maße im Suchfeld: sofort „Suche passende Cases …“ zeigen, dann die Treffer laden. */
    renderDims(dims) {
      this.liveRun = (this.liveRun || 0) + 1;
      const loading = this.el('li', 'tcg-search__message', this.ds.liveLoading);
      loading.setAttribute('role', 'presentation');
      this.list.replaceChildren(this.heading(this.ds.liveHeading), loading);
      this.open();
      clearTimeout(this.dimTimer);
      this.dimTimer = setTimeout(() => this.liveDims(dims), 250);
    }

    /** Holt die passenden Cases (serverseitiger Abgleich) und zeigt die besten 3 im Aufklapp-Fenster. */
    async liveDims(dims) {
      const run = (this.liveRun = (this.liveRun || 0) + 1);
      const ds = this.ds;
      this.optionCount = 0;
      this.activeIndex = -1;
      const loading = this.el('li', 'tcg-search__message', ds.liveLoading);
      loading.setAttribute('role', 'presentation');
      this.list.replaceChildren(this.heading(ds.liveHeading), loading);
      this.open();

      const hits = [];
      try {
        const q = encodeURIComponent(dims.join(' '));
        // 1) Maß-Datenbank über den App-Proxy: eine Anfrage, unabhängig von der Zahl der Produkte
        let viaProxy = false;
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}apps/massfinder?q=${q}`, { headers: { Accept: 'application/json' } });
          if (response.ok) {
            const json = await response.json();
            if (run !== this.liveRun) return;
            if (json && json.source === 'proxy') {
              const doc = new DOMParser().parseFromString(json['tcg-massfinder-results'] || '', 'text/html');
              doc.querySelectorAll('.tcg-mf__hit').forEach((hit) => hits.push(hit));
              viaProxy = true;
            }
          }
        } catch {}
        // 2) Notfall: Abgleich im Theme über die Metafelder (seitenweise)
        for (let page = 1, pages = viaProxy ? 0 : 1; page <= pages && page <= 20; page++) {
          const url = `${this.searchUrl}?q=${q}&type=product&page=${page}&sections=tcg-massfinder-results`;
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(String(response.status));
          const json = await response.json();
          if (run !== this.liveRun) return;
          const doc = new DOMParser().parseFromString(json['tcg-massfinder-results'] || '', 'text/html');
          pages = parseInt(doc.querySelector('[data-mf-pages]')?.dataset.mfPages || '0', 10) || 0;
          doc.querySelectorAll('.tcg-mf__hit').forEach((hit) => hits.push(hit));
        }
      } catch {
        if (run !== this.liveRun) return;
        const { li, a } = this.option(ds.customUrl, 'tcg-search__all');
        a.textContent = ds.liveCustom;
        this.list.replaceChildren(li);
        return;
      }
      if (run !== this.liveRun) return;

      const max = (hit) => Math.max(...hit.dataset.d.split(',').map(Number));
      hits.sort((a, b) => (a.dataset.total - b.dataset.total) || (max(a) - max(b)));
      const best = hits.slice(0, TCG_DIM_MAX);
      const fmt = (tenths) => (tenths / 10).toLocaleString(document.documentElement.lang || 'de', { maximumFractionDigits: 1 });

      this.optionCount = 0;
      this.activeIndex = -1;
      const items = [this.heading(ds.liveHeading)];
      if (!best.length) {
        const msg = this.el('li', 'tcg-search__message', ds.liveNone);
        msg.setAttribute('role', 'presentation');
        items.push(msg);
        const { li, a } = this.option(ds.customUrl, 'tcg-search__all');
        a.textContent = ds.liveCustom;
        items.push(li);
      } else {
        for (const hit of best) {
          const link = hit.querySelector('.tcg-mf__title');
          if (!link) continue;
          const { li, a } = this.option(link.getAttribute('href'), 'tcg-search__product');
          const media = this.el('span', 'tcg-search__media');
          const img = hit.querySelector('.tcg-mf__img img');
          if (img) {
            const pic = this.el('img');
            pic.src = img.currentSrc || img.getAttribute('src');
            pic.alt = '';
            pic.width = 64;
            pic.height = 64;
            pic.loading = 'lazy';
            media.append(pic);
          }
          const info = this.el('span', 'tcg-search__info');
          info.append(this.el('span', 'tcg-search__title', link?.textContent.trim() || ''));
          const inner = hit.querySelector('.tcg-mf__inner')?.dataset.c;
          if (inner) info.append(this.el('span', 'tcg-search__fit', ds.liveInner.replace('__SIZE__', inner.split(',').map((x) => fmt(+x)).join(' × ') + ' mm')));
          info.append(this.el('span', 'tcg-search__price', hit.querySelector('.tcg-mf__price')?.textContent.trim() || ''));
          a.append(media, info);
          items.push(li);
        }
        const more = this.el('li', 'tcg-search__message', ds.liveMore);
        more.setAttribute('role', 'presentation');
        items.push(more);
        const { li, a } = this.option(ds.customUrl, 'tcg-search__all');
        a.textContent = ds.liveCustom;
        items.push(li);
      }
      this.list.replaceChildren(...items);
      this.open();
    }

    onFocus() {
      if (this.term.length === 0) {
        this.showDimbox();
        return;
      }
      if (this.dims(this.term)) this.hideDimbox();
      else this.placeDimbox('top');
      if (this.list.childElementCount > 0) this.open();
    }

    onInput() {
      const term = this.term;
      this.clearButton.hidden = term.length === 0;
      clearTimeout(this.timer);
      if (term.length === 0) {
        this.fetchController?.abort();
        this.liveRun = (this.liveRun || 0) + 1;
        this.showDimbox();
        return;
      }
      const dims = this.dims(term);
      if (dims) {
        this.fetchController?.abort();
        this.hideDimbox();
        this.renderDims(dims);
        return;
      }
      this.liveRun = (this.liveRun || 0) + 1;
      clearTimeout(this.dimTimer);
      this.placeDimbox('top');
      this.timer = setTimeout(() => this.fetchResults(term), 160);
    }

    clear() {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    }

    async fetchResults(term) {
      if (this.cache.has(term)) return this.render(term, this.cache.get(term));

      this.fetchController?.abort();
      this.fetchController = new AbortController();
      this.setAttribute('loading', '');

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,tag,vendor',
      });

      try {
        const response = await fetch(`${this.suggestUrl}?${params}`, {
          signal: this.fetchController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(String(response.status));
        const data = await response.json();
        const results = data?.resources?.results || {};
        this.cache.set(term, results);
        if (term === this.term) this.render(term, results);
      } catch (error) {
        if (error.name !== 'AbortError') this.renderViewAllOnly(term);
      } finally {
        this.removeAttribute('loading');
      }
    }

    formatPrice(value) {
      const amount = parseFloat(value);
      if (Number.isNaN(amount)) return '';
      const currency = window.Shopify?.currency?.active || 'EUR';
      const locale = document.documentElement.lang || 'de';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
      } catch {
        return `${amount.toFixed(2)} ${currency}`;
      }
    }

    imageUrl(product) {
      const src = product.featured_image?.url || product.image;
      if (!src) return '';
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '160');
        return url.toString();
      } catch {
        return src;
      }
    }

    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    option(href, className) {
      const li = this.el('li', 'tcg-search__item');
      li.setAttribute('role', 'presentation');
      const a = this.el('a', className);
      a.href = href;
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      a.id = `${this.id}-opt-${this.optionCount++}`;
      a.tabIndex = -1;
      li.append(a);
      return { li, a };
    }

    heading(text) {
      const li = this.el('li', 'tcg-search__heading', text);
      li.setAttribute('role', 'presentation');
      return li;
    }

    render(term, results) {
      this.optionCount = 0;
      this.activeIndex = -1;
      const items = [];
      const queries = (results.queries || []).filter((q) => q.text.toLowerCase() !== term.toLowerCase()).slice(0, 4);
      const collections = (results.collections || []).slice(0, 3);
      const products = (results.products || []).slice(0, 6);

      if (queries.length) {
        items.push(this.heading(this.t.suggestions));
        for (const query of queries) {
          const { li, a } = this.option(query.url, 'tcg-search__query');
          a.textContent = query.text;
          items.push(li);
        }
      }

      if (collections.length) {
        items.push(this.heading(this.t.collections));
        for (const collection of collections) {
          const { li, a } = this.option(collection.url, 'tcg-search__query');
          a.textContent = collection.title;
          items.push(li);
        }
      }

      if (products.length) {
        items.push(this.heading(this.t.products));
        for (const product of products) {
          const { li, a } = this.option(product.url, 'tcg-search__product');
          const src = this.imageUrl(product);
          const media = this.el('span', 'tcg-search__media');
          if (src) {
            const img = this.el('img');
            img.src = src;
            img.alt = '';
            img.width = 64;
            img.height = 64;
            img.loading = 'lazy';
            media.append(img);
          }
          const info = this.el('span', 'tcg-search__info');
          info.append(this.el('span', 'tcg-search__title', product.title));
          const min = product.price_min ?? product.price;
          const max = product.price_max ?? product.price;
          let price = this.formatPrice(min);
          if (price && parseFloat(max) > parseFloat(min)) price = this.t.from.replace('__PRICE__', price);
          if (product.available === false) price = `${price} · ${this.t.soldOut}`;
          info.append(this.el('span', 'tcg-search__price', price));
          a.append(media, info);
          items.push(li);
        }
      }

      if (items.length === 0) {
        this.renderMessage(this.t.noResults.replace('__TERMS__', term));
        return;
      }

      const { li, a } = this.option(`${this.searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`, 'tcg-search__all');
      a.textContent = this.t.viewAll;
      items.push(li);

      this.list.replaceChildren(...items);
      this.open();
    }

    renderViewAllOnly(term) {
      this.optionCount = 0;
      this.activeIndex = -1;
      const { li, a } = this.option(`${this.searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`, 'tcg-search__all');
      a.textContent = this.t.viewAll;
      this.list.replaceChildren(li);
      this.open();
    }

    renderMessage(text) {
      const li = this.el('li', 'tcg-search__message', text);
      li.setAttribute('role', 'presentation');
      this.list.replaceChildren(li);
      this.open();
    }

    get options() {
      return Array.from(this.list.querySelectorAll('[role="option"]'));
    }

    setActive(index) {
      const options = this.options;
      if (!options.length) return;
      this.activeIndex = (index + options.length) % options.length;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.activeIndex)));
      const active = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (this.hasAttribute('open')) this.setActive(this.activeIndex + 1);
        else if (this.term) this.fetchResults(this.term);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActive(this.activeIndex - 1);
      } else if (event.key === 'Enter') {
        const active = this.options[this.activeIndex];
        if (this.hasAttribute('open') && active) {
          event.preventDefault();
          window.location.href = active.href;
        }
      } else if (event.key === 'Escape') {
        if (this.hasAttribute('open')) {
          event.preventDefault();
          this.close();
        } else if (this.term) {
          this.clear();
        }
      }
    }

    fitPanel() {
      if (!this.hasAttribute('open')) return;
      const top = this.panel.getBoundingClientRect().top;
      this.panel.style.setProperty('--tcg-search-max-height', `${Math.max(240, window.innerHeight - top - 16)}px`);
    }

    open() {
      this.setAttribute('open', '');
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.fitPanel();
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  customElements.define('tcg-header-search', TcgHeaderSearch);
}
